// client/src/components/pages/LexiStar/Billing/Billing.tsx
//
// LexiStar · Assure & configure → Usage & billing, and Settings → LexiStar (ST-00240; was
// under Analyse — ST-00207 stream A; m4b-contracts §2.5). Route /lexistar/billing, page
// permission lexistar.billing.view.
//
// One month at a time (?month=YYYY-MM, default the current SAST month): the organisation's
// usage statement — status, totals and the lines exactly as the server built them — with the
// drill-downs (the trucks and days behind the active-trucks line; the messaging cost log behind
// each message line), Download PDF (the report engine's document; a draft renders on demand)
// and a printable version. A month without a statement yet shows the live figures so far.
//
// Global admins (GET /plan answers can_manage) also get: the organisation picker
// (?tenant=<id>), the plan editor, Rebuild now (a final month: a review copy, not stored) and
// Finalise (a month that has not ended needs a second confirmation). Tenant admins see their
// own organisation's statements only; the server enforces all of it.
//
// ST-00241: trucks are billed a flat monthly rate from their activation until one calendar month
// after a written removal notice. The statement's trucks line then carries each truck's charge
// (the drill-down needs no extra call), and "Trucks on the subscription" (SubscriptionPanel)
// lists every truck's standing — including those removed in the app without a notice, which
// are still billed — where a global admin records or withdraws the notices.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCheck, FaExternalLinkAlt, FaFileDownload, FaFileInvoiceDollar, FaPrint, FaSyncAlt } from 'react-icons/fa';
import { NoPermissionPage } from '../StatusBoard/atoms';
import { PageHeader, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { cardClasses, SkeletonBlock } from '../Dashboard/analyticsAtoms';
import {
  billingErrorCode,
  billingErrorText,
  downloadStatementPdf,
  finaliseStatement,
  getAssetDays,
  getPlan,
  getStatement,
  getUsageSummary,
  listStatements,
  listTenants,
  rebuildMonth,
} from '../../../../services/lexistarBilling';
import type {
  AssetDayRow,
  BillingPlan,
  RebuildResult,
  StatementLine,
  StatementSummary,
  TenantOption,
  UsageStatement,
  UsageSummary,
} from '../../../../types/lexistar-billing';
import StatementDocument, { statusSentence } from './StatementDocument';
import PlanEditor from './PlanEditor';
import SubscriptionPanel from './SubscriptionPanel';
import {
  addMonths,
  CHANNEL_NAMES,
  flatLineOf,
  formatCount,
  formatUnits,
  formatZar,
  isAssetsLine,
  isMonth,
  messagingLogHref,
  monthLabel,
  monthOf,
  monthOptions,
  sastMonth,
  statementPageHref,
} from './billingLogic';

const button =
  'inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed';
const primary =
  'inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed';

/** Months the statement list covers (the picker offers the last 12 plus any older statement). */
const HISTORY_MONTHS = 24;

export default function Billing() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.billing.view');
  const canCostLog = hasPermission('lexistar.reports.view') && hasPermission('lexistar.messages.view');
  const [params, setParams] = useSearchParams();
  const current = sastMonth();
  const monthParam = params.get('month');
  const month = isMonth(monthParam) && monthParam <= current ? monthParam : current;
  const tenantParam = Number(params.get('tenant')) || null;

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ── Plan (+ whether this user manages billing) ──────────────────────────────────
  const [plan, setPlan] = useState<BillingPlan | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const loadPlan = useCallback(() => {
    if (!canView) return Promise.resolve();
    return getPlan(tenantParam)
      .then((r) => {
        setPlan(r.plan);
        setCanManage(r.can_manage);
      })
      .catch((err) => setError(billingErrorText(err, 'The billing plan could not be loaded.')));
  }, [canView, tenantParam]);
  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);
  useEffect(() => {
    if (!canManage || tenants.length) return;
    listTenants()
      .then(setTenants)
      .catch(() => setTenants([]));
  }, [canManage, tenants.length]);
  const tenantId = tenantParam ?? plan?.tenant_id ?? null;
  const tenantName = tenants.find((t) => t.tenant_id === tenantId)?.tenant_name ?? null;

  // ── Statements of the organisation, the month's statement, or the live figures ──
  const [summaries, setSummaries] = useState<StatementSummary[] | null>(null);
  const [statement, setStatement] = useState<UsageStatement | null>(null);
  const [live, setLive] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    listStatements({ from: addMonths(current, -HISTORY_MONTHS), to: current, tenantId: tenantParam })
      .then((list) => alive && setSummaries(list))
      .catch((err) => {
        if (!alive) return;
        setSummaries([]);
        setError(billingErrorText(err, 'The usage statements could not be loaded.'));
      });
    return () => {
      alive = false;
    };
  }, [canView, current, tenantParam, reloadKey]);

  const summary = summaries?.find((s) => monthOf(s.period_month) === month) ?? null;
  useEffect(() => {
    if (!canView || summaries === null) return undefined;
    let alive = true;
    setLoading(true);
    setStatement(null);
    setLive(null);
    const load = summary
      ? getStatement(summary.id).then((s) => {
          if (alive) setStatement(s);
        })
      : getUsageSummary(month, tenantParam).then((s) => {
          if (alive) setLive(s);
        });
    load
      .catch((err) => alive && setError(billingErrorText(err, `The statement for ${monthLabel(month)} could not be loaded.`)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // summary?.id / generated_at carry the statement's identity and freshness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, summaries, summary?.id, summary?.generated_at, month, tenantParam]);

  // ── The trucks behind the trucks line (a flat monthly line carries them itself) ──
  const [showAssets, setShowAssets] = useState(false);
  const [assets, setAssets] = useState<AssetDayRow[] | null>(null);
  const flatLine = statement ? flatLineOf(statement.lines) : null;
  useEffect(() => {
    setShowAssets(false);
    setAssets(null);
  }, [month, tenantParam, reloadKey]);
  useEffect(() => {
    if (!showAssets || assets !== null || flatLine) return undefined;
    let alive = true;
    getAssetDays(month, tenantParam)
      .then((r) => alive && setAssets(r.assets))
      .catch((err) => {
        if (!alive) return;
        setAssets([]);
        setError(billingErrorText(err, 'The trucks behind the statement could not be loaded.'));
      });
    return () => {
      alive = false;
    };
  }, [showAssets, assets, month, tenantParam, flatLine]);

  // ── Actions ─────────────────────────────────────────────────────────────────────
  const [busy, setBusy] = useState<'pdf' | 'rebuild' | 'finalise' | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [review, setReview] = useState<RebuildResult | null>(null);
  const monthOpen = month >= current;

  const act = (fn: () => Promise<void>, kind: 'pdf' | 'rebuild' | 'finalise') => async () => {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const download = act(async () => {
    if (!statement?.id) return;
    try {
      const name = await downloadStatementPdf(statement.id, `Usage statement ${month}.pdf`);
      setNotice(`${name} downloaded.`);
    } catch (err) {
      setError(billingErrorText(err, 'The PDF could not be made. Try again in a minute.'));
    }
  }, 'pdf');

  const rebuild = act(async () => {
    try {
      const r = await rebuildMonth(month, tenantId);
      if (r.stored) {
        setReview(null);
        setNotice(
          `The draft for ${monthLabel(month)} was rebuilt${r.asset_days ? ` (${formatCount(r.asset_days.days)} days of telemetry read again)` : ''}.`
        );
        reload();
      } else {
        setReview(r);
      }
    } catch (err) {
      setError(billingErrorText(err, 'The statement could not be rebuilt.'));
    }
  }, 'rebuild');

  const finalise = act(async () => {
    if (!statement?.id) return;
    try {
      const r = await finaliseStatement(statement.id, monthOpen);
      setConfirming(false);
      setNotice(
        r.pdf.rendered
          ? `${monthLabel(month)} is final. The PDF is made and kept with the statement.`
          : `${monthLabel(month)} is final. The PDF could not be made yet (${r.pdf.error || 'the renderer did not answer'}); it is made on the first download.`
      );
      reload();
    } catch (err) {
      setError(
        billingErrorCode(err) === 'month_open'
          ? `${monthLabel(month)} has not ended yet. Tick the box to finalise it early.`
          : billingErrorText(err, 'The statement could not be finalised.')
      );
    }
  }, 'finalise');

  const options = useMemo(() => monthOptions(current, summaries ?? []), [current, summaries]);

  const lineActions = (l: StatementLine) => {
    if (l.kind === 'assets') {
      return (
        <button
          type="button"
          onClick={() => setShowAssets((v) => !v)}
          aria-expanded={showAssets}
          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
        >
          {showAssets ? 'Hide the trucks' : l.basis === 'flat_monthly' ? 'Show each truck’s charge' : 'Show the trucks and days'}
        </button>
      );
    }
    if (!canCostLog) return null;
    return (
      <Link
        to={messagingLogHref(month, l.channel)}
        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
        title={`The ${CHANNEL_NAMES[l.channel]} messages of ${monthLabel(month)} in the messaging cost log`}
      >
        Open the messaging cost log <FaExternalLinkAlt size={9} aria-hidden="true" />
      </Link>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      icon={FaFileInvoiceDollar}
      title="Usage & billing"
      subtitle="What your organisation used in a month — the trucks on the subscription and the messages — and the statement it is billed on."
      toolbar={
        canView ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[14rem]">
              <ThemedSelect aria-label="Month" value={month} onChange={(e) => update({ month: e.target.value === current ? null : e.target.value })}>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            {canManage && tenants.length > 0 && (
              <div className="min-w-[14rem]">
                <ThemedSelect
                  aria-label="Organisation"
                  value={tenantId ? String(tenantId) : ''}
                  onChange={(e) => {
                    setReview(null);
                    update({ tenant: e.target.value || null });
                  }}
                >
                  {tenants.map((t) => (
                    <option key={t.tenant_id} value={String(t.tenant_id)}>
                      {t.tenant_name}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
            )}
          </div>
        ) : null
      }
      inline={false}
    />
  );
  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view usage statements." />;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1180px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div role="status" className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm">
            {notice}
          </div>
        )}

        {statement && (
          <section aria-label="Statement actions" className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-700">{statusSentence(statement)}</p>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className={primary} onClick={download} disabled={busy !== null}>
                <FaFileDownload size={12} aria-hidden="true" />
                {busy === 'pdf' ? (statement.status === 'final' ? 'Downloading…' : 'Making the PDF…') : 'Download PDF'}
              </button>
              <Link to={statementPageHref(month, tenantParam)} className={button}>
                <FaPrint size={11} aria-hidden="true" /> Printable version
              </Link>
              {canManage && (
                <button type="button" className={button} onClick={rebuild} disabled={busy !== null}>
                  <FaSyncAlt size={11} aria-hidden="true" /> {busy === 'rebuild' ? 'Rebuilding…' : 'Rebuild now'}
                </button>
              )}
              {canManage && statement.status === 'draft' && (
                <button type="button" className={button} onClick={() => setConfirming(true)} disabled={busy !== null || confirming}>
                  <FaCheck size={11} aria-hidden="true" /> Finalise…
                </button>
              )}
            </div>
          </section>
        )}

        {confirming && statement && (
          <FinaliseConfirm
            month={month}
            organisation={statement.tenant_name}
            monthOpen={monthOpen}
            busy={busy === 'finalise'}
            onConfirm={finalise}
            onCancel={() => setConfirming(false)}
          />
        )}

        {review && (
          <section role="status" aria-label="Review copy" className={`${cardClasses} p-4 text-sm text-slate-700 space-y-1`}>
            <p className="font-semibold text-slate-900">Review copy of {monthLabel(month)} — not stored</p>
            <p>
              Computed now: {formatUnits(review.statement.lines.find(isAssetsLine)?.units ?? 0)}{' '}
              {flatLineOf(review.statement.lines) ? 'trucks billed' : 'active trucks'}, total {formatZar(review.statement.total_zar)}. The final
              statement ({formatZar(statement?.total_zar)}) does not change.
            </p>
            <button type="button" className="text-xs font-medium text-primary-700 hover:underline" onClick={() => setReview(null)}>
              Close
            </button>
          </section>
        )}

        {loading || summaries === null ? (
          <div className="space-y-3" aria-busy="true">
            <SkeletonBlock className="h-24" />
            <SkeletonBlock className="h-64" />
          </div>
        ) : statement ? (
          <StatementDocument statement={statement} print={false} assets={assets} showAssets={showAssets} lineActions={lineActions} />
        ) : (
          <section className={`${cardClasses} p-6 space-y-3`} aria-label="No statement yet">
            <h2 className="text-base font-semibold text-slate-900">No statement for {monthLabel(month)} yet</h2>
            <p className="text-sm text-slate-700">
              {live?.billing_basis === 'truck_days'
                ? 'Statements are built every night from the trucks that sent a valid GPS fix and the messages that were sent.'
                : 'Statements are built every night from the trucks on the subscription and the messages that were sent.'}
              {month === current ? ' This month’s draft appears after tonight’s run.' : ''}
            </p>
            {live && (
              <p className="text-sm text-slate-700" data-testid="live-summary">
                {live.billing_basis === 'truck_days' ? (
                  <>
                    So far in {monthLabel(month)}: {formatUnits(live.active_trucks)} active trucks ({formatCount(live.asset_days)} truck-days)
                  </>
                ) : (
                  <>
                    So far in {monthLabel(month)}: {formatUnits(live.active_trucks)} trucks billed ({formatCount(live.full_months)} for the full
                    month, {formatCount(live.first_months)} in their first month)
                  </>
                )}{' '}
                and {formatCount(live.messages.whatsapp + live.messages.sms + live.messages.email)} WhatsApp, SMS and email messages —{' '}
                {formatZar(live.total_zar)} with VAT at the current plan.
              </p>
            )}
            {canManage && (
              <button type="button" className={primary} onClick={rebuild} disabled={busy !== null}>
                <FaSyncAlt size={11} aria-hidden="true" /> {busy === 'rebuild' ? 'Building…' : 'Build the draft now'}
              </button>
            )}
          </section>
        )}

        {summaries !== null && <SubscriptionPanel tenantId={tenantParam} reloadKey={reloadKey} />}

        {canManage && plan && tenantId !== null && (
          <PlanEditor
            plan={plan}
            tenantId={tenantId}
            tenantName={tenantName ?? statement?.tenant_name ?? null}
            onSaved={setPlan}
            onReload={() => void loadPlan()}
          />
        )}
      </div>
    </div>
  );
}

function FinaliseConfirm({
  month,
  organisation,
  monthOpen,
  busy,
  onConfirm,
  onCancel,
}: {
  month: string;
  organisation: string | null;
  monthOpen: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [early, setEarly] = useState(false);
  const ready = !monthOpen || early;
  return (
    <section role="group" aria-label="Finalise the statement" className={`${cardClasses} p-4 space-y-3 border-amber-300`}>
      <p className="text-sm font-semibold text-slate-900">
        Finalise {monthLabel(month)}
        {organisation ? ` for ${organisation}` : ''}?
      </p>
      <p className="text-sm text-slate-700">
        The statement is rebuilt one last time with the current plan, then frozen: a final statement never changes again. The PDF is made
        and kept with it.
      </p>
      {monthOpen && (
        <label className="flex items-start gap-2 text-sm text-amber-800">
          <input type="checkbox" className="mt-1" checked={early} onChange={(e) => setEarly(e.target.checked)} />
          <span>
            {monthLabel(month)} has not ended yet. I understand that trucks and messages after today will not be billed for this month.
          </span>
        </label>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={primary} onClick={onConfirm} disabled={!ready || busy}>
          <FaCheck size={11} aria-hidden="true" /> {busy ? 'Finalising…' : 'Finalise'}
        </button>
        <button type="button" className={button} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}
